import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'

import {
  getLedger,
  getWallet,
  listApiTokens,
  listApplications,
  listPermissions,
  listRecharges,
  listRequests,
} from '../../api/portal'
import { can } from '../../auth/session'
import { usePortal } from '../../auth/sessionContext'
import { Fact } from '../../components/ui/Fact'
import { SectionCard } from '../../components/ui/SectionCard'
import { StatusChip } from '../../components/ui/StatusChip'
import { requestMoney } from '../requests/money'
import { UNSET, formatCount, formatDateTime, formatIrr } from '../../format'

function startOfMonth(): number {
  const now = new Date()
  now.setDate(now.getDate() - 30)
  return now.getTime()
}

/**
 * The home a business customer sees.
 *
 * It answers, in this order and without navigation: who am I, is my account
 * working, what can I spend, what did I spend, and what happened lately. The
 * balance is the largest thing on the page because it is the thing that
 * decides whether the next call succeeds, and because the product owner found
 * it buried a page deep.
 *
 * Applications, request ids, capability codes and documentation are all still
 * in this build and still reachable — they are simply not what a business
 * opens the portal to find.
 */
export function ServiceHome() {
  const { membership } = usePortal()
  const customerId = membership.customer.id
  const seesWallet = can(membership, 'view_wallet')

  const wallet = useQuery({
    queryKey: ['client', 'wallet', customerId],
    queryFn: ({ signal }) => getWallet(customerId, signal),
    enabled: seesWallet,
  })
  const requests = useQuery({
    queryKey: ['client', 'requests', customerId, 1, '', ''],
    queryFn: ({ signal }) => listRequests(customerId, 1, {}, signal),
  })
  const permissions = useQuery({
    queryKey: ['client', 'permissions', customerId],
    queryFn: ({ signal }) => listPermissions(customerId, signal),
  })
  const applications = useQuery({
    queryKey: ['client', 'applications', customerId],
    queryFn: ({ signal }) => listApplications(customerId, signal),
  })
  const recharges = useQuery({
    queryKey: ['client', 'recharges', customerId],
    queryFn: ({ signal }) => listRecharges(customerId, signal),
    enabled: seesWallet,
  })
  const ledger = useQuery({
    queryKey: ['client', 'ledger', customerId],
    queryFn: ({ signal }) => getLedger(customerId, 1, signal),
    enabled: can(membership, 'view_ledger'),
  })

  const primary = applications.data?.results?.[0]
  const tokens = useQuery({
    queryKey: ['client', 'api-tokens', primary?.id],
    queryFn: ({ signal }) => listApiTokens(customerId, primary!.id, signal),
    enabled: primary !== undefined && can(membership, 'view_applications'),
  })
  const liveTokens = (Array.isArray(tokens.data) ? tokens.data : []).filter(
    (token) => token.status === 'active',
  )

  const rows = requests.data?.results ?? []
  const cutoff = startOfMonth()
  const month = rows.filter((row) => new Date(row.created_at).getTime() >= cutoff)
  const monthSpend = month.reduce((total, row) => total + requestMoney(row).chargedIrr, 0)
  const enabledServices = (permissions.data?.results ?? []).filter((p) => p.is_enabled).length
  const lastRecharge = (Array.isArray(recharges.data) ? recharges.data : []).find(
    (row) => row.status === 'settled',
  )
  const pendingRecharge = (Array.isArray(recharges.data) ? recharges.data : []).find(
    (row) => row.status === 'pending',
  )
  const movements = (ledger.data?.results ?? []).slice(0, 5)

  return (
    <Box>
      <Typography variant="h1" gutterBottom>
        سلام، {membership.customer.name}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          وضعیت حساب:
        </Typography>
        <StatusChip status={membership.customer.status} />
      </Stack>

      <Stack spacing={3}>
        {/* The balance, and the two things a customer does about it. */}
        <Paper
          variant="outlined"
          sx={{ p: { xs: 3, sm: 4 }, bgcolor: 'action.hover', borderColor: 'primary.light' }}
        >
          <Typography variant="body2" color="text.secondary">
            موجودی قابل استفاده
          </Typography>
          <Typography
            component="p"
            sx={{
              fontVariantNumeric: 'tabular-nums',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              fontSize: 'clamp(2rem, 6vw, 3rem)',
              lineHeight: 1.2,
              my: 1,
            }}
          >
            {seesWallet && wallet.data
              ? formatIrr(wallet.data.available_balance_irr)
              : UNSET.amount}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {pendingRecharge
              ? `یک درخواست افزایش اعتبار به مبلغ ${formatIrr(pendingRecharge.amount_irr)} در انتظار تأیید است.`
              : 'استعلام‌های بعدی شما از این مبلغ پرداخت می‌شود.'}
          </Typography>

          <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap', mt: 3 }}>
            {seesWallet ? (
              <Button component={Link} to="/recharge" variant="contained" size="large">
                افزایش اعتبار
              </Button>
            ) : null}
            {can(membership, 'view_applications') ? (
              <Button component={Link} to="/connection" variant="outlined" size="large">
                {liveTokens.length > 0 ? 'توکن API' : 'دریافت توکن API'}
              </Button>
            ) : null}
          </Stack>
        </Paper>

        <Grid container spacing={3}>
          <Grid size={{ xs: 6, md: 3 }}>
            <SectionCard>
              <Fact label="مصرف ۳۰ روز اخیر">{formatIrr(monthSpend)}</Fact>
            </SectionCard>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <SectionCard>
              <Fact label="تعداد استعلام‌ها" hint="از ابتدا">
                {requests.data ? formatCount(requests.data.count) : UNSET.amount}
              </Fact>
            </SectionCard>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <SectionCard>
              <Fact label="سرویس‌های فعال">
                {permissions.data ? formatCount(enabledServices) : UNSET.amount}
              </Fact>
            </SectionCard>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <SectionCard>
              <Fact label="آخرین افزایش اعتبار">
                {lastRecharge ? formatIrr(lastRecharge.amount_irr) : UNSET.never}
              </Fact>
            </SectionCard>
          </Grid>
        </Grid>

        <SectionCard
          title="آخرین فعالیت‌ها"
          action={
            <Button component={Link} to="/usage" size="small">
              مصرف و هزینه‌ها
            </Button>
          }
        >
          {rows.length === 0 && movements.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              هنوز فعالیتی روی این حساب ثبت نشده است. پس از نخستین استعلام، هزینه و نتیجهٔ آن اینجا
              دیده می‌شود.
            </Typography>
          ) : (
            <Stack divider={<Divider />}>
              {rows.slice(0, 6).map((row) => {
                const money = requestMoney(row)
                return (
                  <Stack
                    key={row.request_id}
                    direction="row"
                    spacing={2}
                    sx={{ alignItems: 'center', py: 1.25, minWidth: 0 }}
                  >
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="body2" noWrap>
                        {row.operation_display_name_fa || row.operation_code}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(row.created_at)}
                      </Typography>
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                      <StatusChip status={row.status} />
                    </Box>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 600,
                        flexShrink: 0,
                        minWidth: 92,
                        textAlign: 'end',
                      }}
                    >
                      {money.netIrr === 0 ? 'بدون هزینه' : formatIrr(-money.netIrr)}
                    </Typography>
                  </Stack>
                )
              })}
            </Stack>
          )}
        </SectionCard>
      </Stack>
    </Box>
  )
}
